/**
 * Operations · Packaging.
 */
import { useMemo, useState } from "react";
import { PackageOpen, Gift } from "lucide-react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { useDeepLink } from "../../lib/integration";
import { PACKAGING, PackStage, MATERIALS, PackagingJob, SUPPLIERS } from "../../mocks/operationsData";

const STAGE_LABEL: Record<PackStage, string> = {
  queued: "Queued", labelling: "Labelling", boxing: "Boxing",
  sealed: "Sealed", shipped: "Shipped",
};
const STAGE_TINT: Record<PackStage, { bg: string; fg: string }> = {
  queued:    { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"     },
  labelling: { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  boxing:    { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  sealed:    { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  shipped:   { bg: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
};

export default function Packaging() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab] = useState<PackStage | "all">("all");
  const [q, setQ]     = useState(initialQ);
  const [selected, setSelected] = useState<PackagingJob | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: PACKAGING.length };
    PACKAGING.forEach((p) => { c[p.stage] = (c[p.stage] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => PACKAGING.filter((p) =>
      (tab === "all" || p.stage === tab) &&
      (!q || p.batchCode.toLowerCase().includes(q.toLowerCase()) || p.productName.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  const packagingMaterials = MATERIALS.filter((m) => m.category === "packaging");

  return (
    <div className="space-y-6" data-testid="op-packaging">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card testId="op-pack-filters">
            <div className="flex items-center gap-3">
              <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search batch or product…" testId="op-pack-search" /></div>
            </div>
            <div className="mt-4">
              <TabBar<PackStage | "all">
                testId="op-pack-tabs"
                active={tab}
                onChange={setTab}
                tabs={[
                  { key: "all",       label: "All",       count: counts.all       },
                  { key: "queued",    label: "Queued",    count: counts.queued    || 0 },
                  { key: "labelling", label: "Labelling", count: counts.labelling || 0 },
                  { key: "boxing",    label: "Boxing",    count: counts.boxing    || 0 },
                  { key: "sealed",    label: "Sealed",    count: counts.sealed    || 0 },
                  { key: "shipped",   label: "Shipped",   count: counts.shipped   || 0 },
                ]}
              />
            </div>
          </Card>

          <Card padded={false} testId="op-pack-list-card">
            <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <SectionHeader eyebrow="Packaging line" title={`${filtered.length} job${filtered.length === 1 ? "" : "s"}`} action={<PackageOpen size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
            </div>
            <div className="p-2" data-testid="op-pack-list">
              {filtered.map((p) => {
                const t = STAGE_TINT[p.stage];
                return (
                  <ListRow
                    key={p.id}
                    testId={`op-pack-${p.id}`}
                    onClick={() => setSelected(p)}
                    label={
                      <span className="inline-flex items-center gap-2">
                        <span className="font-mono text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{p.batchCode}</span>
                        <span>{p.productName}</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: t.bg, color: t.fg, borderRadius: "var(--ph-radius-sm)" }}>
                          {STAGE_LABEL[p.stage]}
                        </span>
                        {p.giftWrap && (
                          <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: "var(--ph-brand-primary)" }}>
                            <Gift size={10} /> Gift
                          </span>
                        )}
                      </span>
                    }
                    sublabel={<>{p.units} units · owner {p.owner} · ETA {p.eta}</>}
                  />
                );
              })}
            </div>
          </Card>
        </div>

        <Card padded={false} testId="op-pack-materials">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="Materials" title="Packaging inventory" />
          </div>
          <ul className="divide-y" style={{ borderColor: "var(--ph-border-subtle)" }}>
            {packagingMaterials.map((m) => {
              const tint = m.level === "critical" ? "var(--ph-danger)" : m.level === "low" ? "var(--ph-warning)" : "var(--ph-success)";
              const pct = Math.min(100, Math.max(4, (m.onHand / (m.reorderPoint * 2)) * 100));
              return (
                <li key={m.id} className="p-4" data-testid={`op-pack-mat-${m.sku}`}>
                  <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{m.sku}</div>
                  <div className="text-sm font-medium mt-0.5" style={{ color: "var(--ph-ink-strong)" }}>{m.name}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: tint }} />
                    </div>
                    <div className="text-xs tabular-nums flex-shrink-0" style={{ color: "var(--ph-ink-default)" }}>{m.onHand}{m.unit}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Packaging 360" testId="op-pack-drawer" width={560}>
        {selected && (() => {
          const t = STAGE_TINT[selected.stage];
          const relatedMats = MATERIALS.slice(0, 5);
          const bottleCost = 8, capCost = 3, labelCost = 2, boxCost = 15, shrinkCost = 1;
          const total = (bottleCost + capCost + labelCost + boxCost + shrinkCost) * selected.units;
          return (
            <div>
              <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.batchCode}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.productName}</div>
              <div className="mb-3">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: t.bg, color: t.fg, borderRadius: "var(--ph-radius-sm)" }}>{STAGE_LABEL[selected.stage]}</span>
                {selected.giftWrap && <span className="ml-2 inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--ph-brand-primary)" }}><Gift size={11} /> Gift wrap</span>}
              </div>
              <Field label="Batch code"     inline><span className="text-sm font-mono">{selected.batchCode}</span></Field>
              <Field label="Product"        inline><span className="text-sm">{selected.productName}</span></Field>
              <Field label="Units"          inline><span className="text-sm tabular-nums">{selected.units}</span></Field>
              <Field label="Stage"          inline><span className="text-sm capitalize">{selected.stage.replace("-", " ")}</span></Field>
              <Field label="Bottle"         inline><span className="text-sm">50ml glass · EGP {bottleCost}/unit</span></Field>
              <Field label="Cap"            inline><span className="text-sm">Brass polished · EGP {capCost}/unit</span></Field>
              <Field label="Label"          inline><span className="text-sm">Premium foil · EGP {labelCost}/unit</span></Field>
              <Field label="Box"            inline><span className="text-sm">{selected.giftWrap ? "Gift box" : "Standard box"} · EGP {boxCost}/unit</span></Field>
              <Field label="Shrink wrap"    inline><span className="text-sm">Yes · EGP {shrinkCost}/unit</span></Field>
              <Field label="Total cost"     inline><span className="text-sm font-semibold tabular-nums">EGP {total.toLocaleString()}</span></Field>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Supplier</div>
                <div className="text-sm">{SUPPLIERS[0]?.name || "—"}</div>
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Material inventory · {relatedMats.length}</div>
                {relatedMats.map((m) => (
                  <div key={m.id} data-testid={`op-pack-mat-${m.id}`} className="py-1.5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                    <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{m.name}</div>
                    <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{m.onHand}{m.unit} on hand · level {m.level}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Packaging timeline</div>
                <AuditTrail collection="packaging" recordId={selected.id} testId="op-pack-audit" />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button variant="secondary" testId="op-pack-hold">Put on hold</Button>
                <Button testId="op-pack-advance">Advance stage</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}
